import { BrowserRouter, Route, Routes, matchPath } from 'react-router-dom'
import Layout from './Layout'
import Show from './client'
import Documents from './client/DocumentsForm'
import Client from './client/Form'
import CompanyCreate from './company/Create'
import CompanyDelete from './company/Delete'
import CompanyEdit from './company/Edit'
import Users from './user'
import ForgotPassword from './user/ForgotPassword'
import User from './user/Form'
import Login from './user/Login'
// import InactiveClient from './client/InactiveClients'
import Stammdaten from './client/Stammdaten'
import StammdatenCompany from './client/StammdatenCompany'
import StammdatenPinAccess from './client/StammdatenPinAccess'
import StammdatenPerson from './client/StammdatenPerson'
import SurveyFormCompany from './client/SurveyFormCompany'
import IdentityDocuments from './client/IdentityDocuments'
import SurveyFormPerson from './client/SurveyFormPerson'
import View from './client/View'
import Company from './company'
import Settings from './company/Settings'
import AbacusSettings from './company/AbacusSettings'
import SwissTransparenzregisterSettings from './company/SwissTransparenzregisterSettings'
import MicrosoftSsoSettings from './company/MicrosoftSsoSettings'
import MicrosoftSharePointAppSettings from './company/MicrosoftSharePointAppSettings'
import BexioSettings from './company/BexioSettings'
import BexioCallback from './company/BexioCallback'
import { CheckoutForm, Return } from './components/Stripe'
import NotFound from './error/404'
import {
  disableClickables,
  enableClickables,
  getAccessToken,
  getSuperAdminToken,
  isPublicAuthPath,
  isUserLoggedIn,
  removeAccess,
  removeSuperAdminAccess,
  setRedirectUrl,
  showKAlert,
  wasRecentlyAuthenticated,
} from './helpers'
import { getLocale, t } from './locale'
import StammdatenAdditional from './stammdaten/StammdatenAdditional'
import FormSchemasListPage from './stammdaten/StammdatenDynamicFormList'
import StammdatenRequiredCompany from './stammdaten/StammdatenRequiredCompany'
import StammdatenRequiredPerson from './stammdaten/StammdatenRequiredPerson'
import StammdatenView from './stammdaten/StammdatenView'
import Billing from './user/Billing'
import PinCode from './user/PinCode'
import UserProfile from './user/Profile'
import SetPassword from './user/SetPassword'
import UserShow from './user/Show'
import TwoFASetup from './user/TwoFASetup'
import TwoFAVerify from './user/TwoFAVerify'
import SuperAdminLogin from './super-admin/Login'
import SuperAdminPinCode from './super-admin/PinCode'
import SuperAdminDashboard from './super-admin/Dashboard'
import SuperAdminSetPassword from './super-admin/SetPassword'
import Subdomain from './user/Subdomain'
import { SubscriptionProvider } from './context/SubscriptionContext'
import VertecSettings from './company/VertecSettings'

const baseDomain = process.env.REACT_APP_BASE_URL ?? ''
const baseDomainCh = process.env.REACT_APP_BASE_URL_CH ?? ''
const host = window.location.host

function extractSubdomain(host: string, base: string): string {
  return host !== base && host.endsWith(`.${base}`)
    ? host.slice(0, host.length - base.length - 1)
    : ''
}

const hostPrefix = extractSubdomain(host, baseDomain) || extractSubdomain(host, baseDomainCh)

const subdomain = (window.subdomain = hostPrefix && hostPrefix !== 'www' ? hostPrefix : undefined)

const { fetch: originalFetch } = window

// Several background requests can 401/403 at once when a session expires. Each one
// used to schedule its own delayed redirect to /user/logout, so a user who
// re-logged in during that 2s window (login -> PIN) had the just-created session
// torn down by a stale timer and was bounced back to the login screen - only the
// next attempt, with no timers left pending, would stick. Keep a single pending
// redirect, tear the session down only inside the timer (after a final re-check),
// and send the user to /user/login rather than /user/logout so a spurious bounce
// never hits DELETE /access and kills the freshly issued server session.
let pendingSessionRedirect: ReturnType<typeof setTimeout> | null = null

const scheduleSessionRedirect = (
  target: string,
  shouldRedirect: () => boolean,
  onRedirect?: () => void,
  delayMs = 2000
) => {
  if (pendingSessionRedirect) return
  pendingSessionRedirect = setTimeout(() => {
    pendingSessionRedirect = null
    // A fresh login may have completed - or these may have been stale requests
    // already in flight when it did - while this was pending. Re-check before
    // tearing anything down.
    if (isPublicAuthPath(window.location.pathname) || !shouldRedirect()) return
    onRedirect?.()
    window.location.href = target
  }, delayMs)
}

const routesDeductPoints = [
  '/client',
  '/subscription/update',
  '/identity/create',
  '/transparenzregister/document-request',
  '/schufa/search',
  '/sign',
  '/aml/order',
  '/subscription/update',
]
window.fetch = async (resource: any, options: any = {}) => {
  window.dispatchEvent(new Event('startFetch'))

  disableClickables()

  const headers = options.headers ?? {}
  const resourceUrl = typeof resource === 'string' ? resource : String(resource?.url ?? '')
  const isSuperAdminRequest = resourceUrl.includes('/super-admin')

  !!subdomain && (headers['subdomain'] = subdomain)

  // Pre-auth tokens (before PIN/TOTP verified) are still sent as header from sessionStorage.
  // Verified session tokens travel exclusively as HttpOnly cookies (never in sessionStorage).
  const preAuthToken = getAccessToken()
  if (preAuthToken) headers['accessToken'] = preAuthToken

  const superAdminToken = getSuperAdminToken()
  if (isSuperAdminRequest && superAdminToken) {
    headers['x-super-admin-token'] = superAdminToken
  }

  options.headers = headers
  options.credentials = options.credentials ?? 'include'

  let r: Response

  try {
    r = await originalFetch(resource, options)
  } catch (err) {
    // Backend unreachable (offline, connection refused, CORS, etc.) - never let this
    // reject, otherwise every call site that doesn't wrap fetch() in its own try/catch
    // surfaces the raw "Failed to fetch" TypeError to the user (and crashes the CRA
    // dev overlay). Show the friendly alert and resolve with a synthetic error response.
    window.dispatchEvent(new Event('endFetch'))
    showKAlert('danger', 'networkError')
    scheduleSessionRedirect(
      '/user/login',
      () => !isPublicAuthPath(window.location.pathname) && !wasRecentlyAuthenticated(),
      removeAccess,
      5000
    )
    return new Response(JSON.stringify({}), { status: 503, statusText: 'Network Error' })
  }

  window.dispatchEvent(new Event('endFetch'))

  if (r.status === 401 || r.status === 403) {
    if (isSuperAdminRequest) {
      showKAlert('warning', 'sessionExpired')
      removeSuperAdminAccess()
      scheduleSessionRedirect('/super-admin/login', () => !getSuperAdminToken())
    } else if (!isPublicAuthPath(window.location.pathname) && !wasRecentlyAuthenticated()) {
      // Don't tear down synchronously: a stale in-flight request (pre-auth token,
      // fired just before the PIN/2FA step completed) must not wipe the session
      // that step just established. Defer everything to the timer's re-check.
      scheduleSessionRedirect(
        '/user/login',
        () => !wasRecentlyAuthenticated(),
        () => {
          showKAlert('warning', 'sessionExpired')
          removeAccess()
        }
      )
    }
    return r
  }

  if (r.status === 404 || r.status === 500) {
    showKAlert('danger', 'serverError')
  }

  if (
    r.headers.get('X-Subscription-Updated') === 'true' ||
    routesDeductPoints.some(route => resource.includes(route))
  ) {
    localStorage.removeItem('subscriptionData')

    window.dispatchEvent(new Event('subscriptionUpdated'))
  }

  enableClickables()

  r.clone()
    .text()
    .then(text => {
      try {
        const parsed = JSON.parse(text)
        !!parsed.err && showKAlert('danger', parsed.err)
        !!parsed.message && showKAlert(parsed.message.variant, t(parsed.message.content))
      } catch (error) {}
    })

  return r
}

document.documentElement.setAttribute('lang', getLocale())

function App() {
  const pathname = window.location.pathname

  if (!!subdomain) {
    const hasOneParam =
      !!matchPath({ path: '/client/stammdaten/company/:id', end: true }, pathname) ||
      !!matchPath({ path: '/client/stammdaten/person/:id', end: true }, pathname)

    // The bare `/client/stammdaten/{company,person}/:id` route is the form itself.
    // A shared client reaches it from StammdatenPinAccess (the `/:id/:email` route)
    // after entering their PIN, which leaves a client access token in sessionStorage -
    // let that through, and the pre-auth token during the internal 2FA flow. Only a
    // visitor with no token at all gets bounced to login.
    if (hasOneParam && !isUserLoggedIn() && !getAccessToken()) {
      setRedirectUrl(pathname)
      window.location.href = '/user/login'
      return <></>
    }

    if (
      !isUserLoggedIn() &&
      !isPublicAuthPath(pathname) &&
      !pathname.startsWith('/client/stammdaten/company/') &&
      !pathname.startsWith('/client/stammdaten/person/')
    ) {
      setRedirectUrl(pathname)
      window.location.href = '/user/login'
      return <></>
    }

    if (isUserLoggedIn() && isPublicAuthPath(pathname)) {
      window.location.href = '/'
      return <></>
    }
  }

  return (
    <SubscriptionProvider>
      <BrowserRouter>
        <Routes>
          <Route path='/' element={<Layout />}>
            {/* <Route index element={!!subdomain ? <Dashboard /> : <Home />} /> */}
            <Route path='return' element={<Return />} />
            <Route path='not-found' element={<NotFound />} />
            {!subdomain && (
              <>
                <Route path='/login' element={<Subdomain />} />
                <Route path='company/create' element={<CompanyCreate />} />
                <Route path='super-admin/login' element={<SuperAdminLogin />} />
                <Route
                  path='super-admin/pincode-validation/:pendingToken'
                  element={<SuperAdminPinCode />}
                />
                <Route path='super-admin/set-password' element={<SuperAdminSetPassword />} />
                <Route path='super-admin' element={<SuperAdminDashboard />} />
              </>
            )}
            {!!subdomain && (
              <>
                <Route path='user/login' element={<Login />} />
                <Route path='user/logout' element={<Login logout={true} />} />
                <Route path='user/forgot-password' element={<ForgotPassword />} />
                <Route path='user/confirm/:accessToken' element={<SetPassword reset={false} />} />
                <Route
                  path='user/password-set/:accessToken'
                  element={<SetPassword reset={true} />}
                />
                <Route path='user/pincode-validation/:pinCode?' element={<PinCode />} />
                <Route path='user' element={<Users />} />
                <Route path='user/create' element={<User />} />
                <Route path='user/:userId/edit' element={<User edit={true} />} />
                <Route path='user/:userId/show' element={<UserShow />} />
                <Route path='user/profile' element={<UserProfile />} />
                <Route path='company' element={<Company />} />
                <Route path='company/edit' element={<CompanyEdit />} />
                <Route path='company/delete' element={<CompanyDelete />} />
                <Route path='company/team' element={<Settings />} />
                <Route path='company/abacus' element={<AbacusSettings />} />
                <Route
                  path='company/swiss-transparenzregister'
                  element={<SwissTransparenzregisterSettings />}
                />
                <Route path='company/microsoft' element={<MicrosoftSsoSettings />} />
                <Route path='company/sharepoint-app' element={<MicrosoftSharePointAppSettings />} />
                <Route path='company/bexio' element={<BexioSettings />} />
                <Route path='bexio/callback' element={<BexioCallback />} />
                <Route path='checkout' element={<CheckoutForm />} />
                <Route path='clients' element={<Show />} />
                <Route path='client/:clientId/edit' element={<Client edit={true} />} />
                <Route path='client/create' element={<Client />} />
                <Route path='client/view/:id' element={<View />} />
                <Route path='client/stammdaten' element={<Stammdaten />} />
                <Route
                  path='client/stammdaten/company/:clientId/:email'
                  element={<StammdatenPinAccess stammdatenType='company' />}
                />
                <Route
                  path='client/stammdaten/person/:clientId/:email'
                  element={<StammdatenPinAccess stammdatenType='person' />}
                />
                <Route path='client/stammdaten/company/:clientId' element={<StammdatenCompany />} />
                <Route path='client/stammdaten/person/:clientId' element={<StammdatenPerson />} />
                <Route path='client/stammdaten/formular/list' element={<FormSchemasListPage />} />
                <Route path='client/stammdaten/:clientId/view' element={<StammdatenView />} />
                <Route path='user/billing' element={<Billing />} />
                <Route path='client/documents/:id' element={<Documents />} />
                <Route
                  path='client/identity-documents/:clientId/:subjectType/:subjectId'
                  element={<IdentityDocuments />}
                />
                {/* <Route path='client/inactive' element={<InactiveClient />}/> */}
                <Route path='user/twofa/setup' element={<TwoFASetup />} />
                /user/twofa-verify/
                <Route path='/user/twofa-verify/:pending' element={<TwoFAVerify />} />
                <Route
                  path='client/stammdaten/formular/:stammdatenType/:_id?'
                  element={<StammdatenAdditional />}
                />
                <Route
                  path='client/stammdaten/required/company'
                  element={<StammdatenRequiredCompany />}
                />
                <Route
                  path='client/stammdaten/required/person'
                  element={<StammdatenRequiredPerson />}
                />
                <Route
                  path='client/vorlaged-dc/:clientId?/company'
                  element={<SurveyFormCompany />}
                />
                <Route path='client/vorlaged-dc/:clientId?/person' element={<SurveyFormPerson />} />
                <Route path='company/vertec' element={<VertecSettings />} />
              </>
            )}
            <Route path='*' element={<NotFound />} />
          </Route>
        </Routes>
      </BrowserRouter>
    </SubscriptionProvider>
  )
}

export default App
